import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Action, DragAction, GeneralAction, PrimaryAction, SecondaryAction } from "./Actions";
import { Point } from "./Geometry";
import { MeasurementsRenderer } from "./MeasurementRenderer/MeasurementsRenderer";
import { isLineMeasurementRequirement, LineDirection, LineMeasurement } from "../../data/Measurements/Measurement";
import { useMeasurement } from "../../providers/NewMeasurementProvider";
import { CanvasViewportProvider, useCanvasViewport } from "./CanvasViewportContext";
import { ContextMenu } from "./ContextMenu";
import { Sidebar } from "./Sidebar";
import SelvesterTaskData from "../../data/Tasks/SelvesterTask";
import { KeyboardActionTrigger, useKeyboardActionTrigger } from "../../hooks/useKeyboardActionTrigger";
import ACTION_TRIGGERS from "./KeyboardActionTriggers";

// MARK: Constants

const MAX_ZOOM = 5;
const MIN_ZOOM = 0.4;

const MAX_OFFSET = 5000;
const MIN_OFFSET = -5000;

export type CanvasViewportProps = {
  /**
   * The content of the image to be displayed on the canvas.
   */
  image: ImageBitmap;

  /**
   * The width of the viewport.
   */
  width: number;

  /**
   * The height of the viewport.
   */
  height: number;
}

export function CanvasViewport({ image, width, height }: CanvasViewportProps) {
  // MARK: Measurements

  const { nextMeasurement, updateMeasurement, updateMeasurementAsync, undoLastMeasurementChange } = useMeasurement();

  // MARK: Infinite Canvas

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const zoomCanvasRef = useRef<HTMLCanvasElement>(null);

  const {
    setCanvas, offset, setOffset, scale, setScale,
    virtualToReal, pageMousePosition, setPageMousePosition,
    canvasMousePosition, virtualMousePosition,
    pan, setMeasurementKeyThatWasJustCreated, measurementKeyThatWasJustCreated,
    measurementContextMenu, closeMeasurementContextMenu, primaryAction, setPrimaryAction,
    dragAction, setDragAction, measurementKeyBeingDragged
  } = useCanvasViewport();

  useEffect(() => {
    if (canvasRef.current === null) {
      return;
    }

    console.log("Setting canvas reference.");

    setCanvas(canvasRef.current);
  }, [canvasRef.current, setCanvas]);

  // MARK: Drag State

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Point>({ x: 0, y: 0 });

  // MARK: Actions

  /**
   * Executes the `action-pan` action.
   */
  const executePanAction = useCallback((event: React.MouseEvent) => {
    if(measurementKeyBeingDragged) {
      return;
    }

    const lastMousePosition = pageMousePosition;

    const dx = event.clientX - lastMousePosition.x;
    const dy = event.clientY - lastMousePosition.y;

    const newOffset = {
      x: offset.x + dx / scale,
      y: offset.y + dy / scale
    };

    if (newOffset.x > MAX_OFFSET || newOffset.x < MIN_OFFSET) {
      return;
    }

    setOffset(newOffset);
  }, [measurementKeyBeingDragged, pageMousePosition, offset.x, offset.y, scale, setOffset]);

  /**
   * Dispatches a drag action to their respective executors.
   */
  const dispatchDragAction = useCallback((event: React.MouseEvent) => {
    const action = dragAction;

    if (action === 'action-pan') {
      executePanAction(event);
    } else if (action === 'inactive') {
      return;
    }
  }, [executePanAction, dragAction]);

  /**
   * Executes the `action-start-next-measurement` action.
   */
  const executeStartNextMeasurementAction = useCallback(() => {
    if (nextMeasurement === undefined) {
      console.warn("No measurement to start.");

      return;
    }

    if (isLineMeasurementRequirement(nextMeasurement)) {
      updateMeasurementAsync(
        {
          key: nextMeasurement.key,
          type: "line",
          startPosition: virtualMousePosition,
          endPosition: newMeasurementEndPosition(virtualMousePosition, nextMeasurement.direction),
          direction: nextMeasurement.direction,
          drawable: true
        }
      ).then(() => {
        setMeasurementKeyThatWasJustCreated(nextMeasurement.key);
      });
    }
  }, [nextMeasurement, setMeasurementKeyThatWasJustCreated, updateMeasurementAsync, virtualMousePosition]);

  // Effect: Change the measurement key that was just created to null after the VERY next render it was set to something.
  useEffect(() => {
    console.log("Measurement key that was just created: ", measurementKeyThatWasJustCreated);

    if (measurementKeyThatWasJustCreated) {
      setMeasurementKeyThatWasJustCreated(null);
    }
  }, [measurementKeyThatWasJustCreated, setMeasurementKeyThatWasJustCreated]);

  /**
   * Dispatches a primary action to their respective executors.
   */
  const dispatchPrimaryAction = useCallback(() => {
    const action = primaryAction;

    console.log(`Primary action: ${action}`);

    if (action === 'action-select') {
      console.log("Selecting not implemented yet");
    } else if (action === 'action-start-next-measurement') {
      executeStartNextMeasurementAction();
    }
  }, [executeStartNextMeasurementAction, primaryAction]);

  // Secondary Actions

  const dispatchSecondaryAction = useCallback((action: SecondaryAction) => {
    console.log(`Secondary action: ${action}`);
  }, []);

  // General Actions

  /**
   * Executes the `action-undo` action.
   */
  const executeUndoAction = useCallback(() => {
    undoLastMeasurementChange();
  }, [undoLastMeasurementChange]);


  /**
   * Executes the `action-set-measurement-as-unmeasurable` action.
   */
  const executeSetMeasurementAsUnmeasurableAction = useCallback(() => {
    if (nextMeasurement) {
      updateMeasurement({
        key: nextMeasurement.key,
        type: 'unmeasurable',
        drawable: false
      });
    }
  }, [nextMeasurement, updateMeasurement]);

  /**
   * Executes all pan actions.
   */
  const executePanActions = useCallback((action: GeneralAction) => {
    if(measurementKeyBeingDragged) {
      return;
    }

    const duration = 100;
    const panAmount = 70 / Math.max(scale, 1);

    if (action === 'action-pan-up') {
      pan(0, panAmount, duration);
    } else if (action === 'action-pan-down') {
      pan(0, -panAmount, duration);
    } else if (action === 'action-pan-left') {
      pan(panAmount, 0, duration);
    } else if (action === 'action-pan-right') {
      pan(-panAmount, 0, duration);
    }
  }, [scale, pan, measurementKeyBeingDragged]);

  const dispatchGeneralAction = useCallback((action: GeneralAction) => {
    console.log(`General action: ${action}`);

    if (action === 'action-undo') {
      executeUndoAction();
    } else if (action === 'action-set-measurement-as-unmeasurable') {
      executeSetMeasurementAsUnmeasurableAction();
    } else if (action.startsWith('action-pan')) {
      executePanActions(action);
    } else {
      console.warn(`General action ${action} not implemented.`);
    }
  }, [executePanActions, executeSetMeasurementAsUnmeasurableAction, executeUndoAction]);

  // Triggers

  const onActionTriggeredFromKeyboard = useCallback((action: GeneralAction) => {
    dispatchGeneralAction(action);
  }, [dispatchGeneralAction]);

  useKeyboardActionTrigger({
    triggers: ACTION_TRIGGERS,
    onActionTriggered: onActionTriggeredFromKeyboard
  });

  // MARK: Rendering

  useEffect(() => {
    if (canvasRef.current === null) {
      return;
    }

    const canvasWidth = canvasRef.current.clientWidth;
    const canvasHeight = canvasRef.current.clientHeight;

    const context = canvasRef.current.getContext("2d");

    if (context === null) {
      return;
    }

    context.imageSmoothingQuality = "high";
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    drawGrid(context, 20, canvasWidth, canvasHeight, offset, scale);

    // draw the image on the infinite canvas
    context.drawImage(
      image,
      0,
      0,
      image.width,
      image.height,
      virtualToReal({ x: 0, y: 0 }).x,
      virtualToReal({ x: 0, y: 0 }).y,
      image.width * scale,
      image.height * scale
    );
  }, [image, offset, scale, virtualToReal]);

  // MARK: Mouse Events

  const onWheel = useCallback((event: React.WheelEvent<HTMLCanvasElement>) => {
    const delta = event.deltaY;

    const zoomFactor = 1 - delta * 0.001;
    const newScale = scale * zoomFactor;

    if (newScale > MAX_ZOOM || newScale < MIN_ZOOM) {
      return;
    }

    setScale(newScale);
  }, [setScale, scale]);

  const onMouseDown = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    event.preventDefault();

    setIsDragging(true);
    setDragStart({ x: event.clientX, y: event.clientY });
  }, [setIsDragging, setDragStart]);

  const onMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    setPageMousePosition({ x: event.clientX, y: event.clientY });

    if (isDragging) {
      dispatchDragAction(event);
    }
  }, [setPageMousePosition, isDragging, dispatchDragAction]);

  const onMouseUp = useCallback((event: React.MouseEvent) => {
    setIsDragging(false);

    const wasTrueDrag = Math.abs(pageMousePosition.x - dragStart.x) > 6 || Math.abs(pageMousePosition.y - dragStart.y) > 6;

    if (!wasTrueDrag && event.button === 0) {
      dispatchPrimaryAction();
    } else if (!wasTrueDrag && event.button === 2) {

    }
  }, [pageMousePosition, dragStart.x, dragStart.y, dispatchPrimaryAction]);

  const onContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();

    closeMeasurementContextMenu();
  }, [closeMeasurementContextMenu]);

  // MARK: UI Definition

  return (
    <>
      <div className="relative">
        <canvas
          ref={canvasRef}
          tabIndex={0}
          width={width}
          height={height}
          className="z-10"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onWheel={onWheel}
          onContextMenu={onContextMenu}
        />

        <div className="absolute top-0 left-0 w-full h-full z-20 pointer-events-none">
          {/* SVG and HTML elements go here */}
          <MeasurementsRenderer />

          <ContextMenu />
        </div>

        <div className="absolute bottom-0 right-0 m-2 p-1 bg-gray-800 text-white text-xs rounded font-mono">
          {virtualMousePosition.x.toFixed(0)}, {virtualMousePosition.y.toFixed(0)}
        </div>
      </div>
    </>
  )
}

// MARK: Helpers

/**
 * Draws a grid on the canvas.
 * @param context The 2D rendering context of the canvas.
 * @param cellSize The size of each cell in the grid.
 * @param canvasWidth The width of the canvas.
 * @param canvasHeight The height of the canvas.
 * @param offset The offset of the viewport.
 * @param scale The scale of the viewport.
 */
function drawGrid(
  context: CanvasRenderingContext2D,
  cellSize: number,
  canvasWidth: number,
  canvasHeight: number,
  offset: Point,
  scale: number
) {
  context.strokeStyle = "rgb(100,100,100)";
  context.lineWidth = 0.5;

  context.beginPath();

  for (
    /* Start the first line based on offsetX and scale */
    let x = (offset.x % cellSize) * scale;
    x <= canvasWidth;
    /* Cell size based on scale amount */
    x += cellSize * scale
  ) {
    context.moveTo(x, 0);
    context.lineTo(x, canvasHeight);
  }

  for (
    let y = (offset.y % cellSize) * scale;
    y <= canvasHeight;
    y += cellSize * scale
  ) {
    context.moveTo(0, y);
    context.lineTo(canvasWidth, y);
  }

  context.stroke();
}

function newMeasurementEndPosition(startPosition: Point, direction: LineDirection): Point {
  switch (direction) {
    case 'horizontal':
      return { x: startPosition.x + 1, y: startPosition.y };
    case 'vertical':
      return { x: startPosition.x, y: startPosition.y + 1 };
    case 'free':
      return { x: startPosition.x + 1, y: startPosition.y + 1 };
  }
}